import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Phone, User, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { userAPI } from '../api/api';

const CompleteProfileModal = () => {
  const { user, setUser } = useAuth();
  const { isDark } = useTheme();
  
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [phone, setPhone] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      // Check if user signed in with Google (has google_id or auth_provider is google)
      const isGoogleUser = user.google_id || user.auth_provider === 'google';
      
      // Only ask for phone if user signed in with Google AND has no phone
      const needsPhone = isGoogleUser && (!user.phone || user.phone === '' || user.phone === 'N/A');
      
      // Check if name is single word
      const nameParts = (user.name || '').trim().split(/\s+/);
      const needsFullName = nameParts.length < 2;

      // Show phone modal first if needed (only for Google users)
      if (needsPhone) {
        setShowPhoneModal(true);
        setShowNameModal(false);
      } else if (needsFullName) {
        // Show name modal only if phone is complete
        setShowPhoneModal(false);
        setShowNameModal(true);
        setFullName(user.name || '');
      }
    }
  }, [user]);

  const handlePhoneSubmit = async () => {
    // Validate phone
    const phoneRegex = /^(05|5)\d{8}$/;
    const cleanPhone = phone.replace(/\s/g, '');
    
    if (!phoneRegex.test(cleanPhone)) {
      toast.error('الرجاء إدخال رقم جوال صحيح (مثال: 0501234567)');
      return;
    }

    setLoading(true);
    try {
      // Update user profile with phone
      await userAPI.updateProfile({ phone: cleanPhone });
      
      // Update local user state
      const updatedUser = { ...user, phone: cleanPhone };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('تم حفظ رقم الجوال بنجاح! ✅');
      setShowPhoneModal(false);
      
      // Check if name also needs update
      const nameParts = (user.name || '').trim().split(/\s+/);
      if (nameParts.length < 2) {
        setShowNameModal(true);
        setFullName(user.name || '');
      }
    } catch (error) {
      console.error('Error updating phone:', error);
      toast.error('فشل في حفظ رقم الجوال');
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = async () => {
    // Validate name has at least 2 parts
    const nameParts = fullName.trim().split(/\s+/);
    
    if (nameParts.length < 2) {
      toast.error('الرجاء إدخال الاسم الثنائي على الأقل (الاسم الأول + اسم العائلة)');
      return;
    }

    if (fullName.trim().length < 4) {
      toast.error('الاسم قصير جداً');
      return;
    }

    setLoading(true);
    try {
      // Update user profile with full name
      await userAPI.updateProfile({ name: fullName.trim() });
      
      // Update local user state
      const updatedUser = { ...user, name: fullName.trim() };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('تم حفظ الاسم بنجاح! ✅');
      setShowNameModal(false);
    } catch (error) {
      console.error('Error updating name:', error);
      toast.error('فشل في حفظ الاسم');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPhone = () => {
    setShowPhoneModal(false);
    // Check if name needs update
    const nameParts = (user?.name || '').trim().split(/\s+/);
    if (nameParts.length < 2) {
      setShowNameModal(true);
      setFullName(user?.name || '');
    }
  };

  return (
    <>
      {/* Phone Number Modal */}
      <Dialog open={showPhoneModal} onOpenChange={setShowPhoneModal}>
        <DialogContent className={`max-w-md border ${
          isDark 
            ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-700 text-white' 
            : 'bg-gradient-to-br from-white to-gray-50 border-gray-200 text-gray-900'
        }`}>
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className={`p-4 rounded-full ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                <Phone className="h-8 w-8 text-green-500" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl">أضف رقم جوالك 📱</DialogTitle>
            <DialogDescription className={`text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              نحتاج رقم جوالك للتواصل معك في حال الفوز بالسحوبات والجوائز! 🎁
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                رقم الجوال
              </Label>
              <div className="relative">
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="05xxxxxxxx"
                  className={`text-center text-lg tracking-wider ${
                    isDark 
                      ? 'bg-gray-800 border-gray-600 text-white placeholder:text-gray-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'
                  }`}
                  dir="ltr"
                  maxLength={10}
                />
              </div>
              <p className={`text-xs text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                سيتم استخدام الرقم للتواصل معك فقط
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handlePhoneSubmit}
                disabled={loading || !phone}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin ml-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 ml-2" />
                )}
                حفظ
              </Button>
              <Button
                onClick={handleSkipPhone}
                variant="outline"
                className={`flex-1 ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
              >
                لاحقاً
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Name Modal */}
      <Dialog open={showNameModal} onOpenChange={setShowNameModal}>
        <DialogContent className={`max-w-md border ${
          isDark 
            ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-700 text-white' 
            : 'bg-gradient-to-br from-white to-gray-50 border-gray-200 text-gray-900'
        }`}>
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className={`p-4 rounded-full ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                <User className="h-8 w-8 text-blue-500" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl">أكمل اسمك ✏️</DialogTitle>
            <DialogDescription className={`text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              نحتاج اسمك الثنائي للسحوبات وشهادات الفوز! 🏆
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="fullName" className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                الاسم الثنائي
              </Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="مثال: أحمد محمد"
                className={`text-center text-lg ${
                  isDark 
                    ? 'bg-gray-800 border-gray-600 text-white placeholder:text-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'
                }`}

              />
              <p className={`text-xs text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                اكتب اسمك الأول واسم عائلتك
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleNameSubmit}
                disabled={loading || !fullName.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin ml-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 ml-2" />
                )}
                حفظ
              </Button>
              <Button
                onClick={() => setShowNameModal(false)}
                variant="outline"
                className={`flex-1 ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
              >
                لاحقاً
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CompleteProfileModal;
